import type { CSSProperties, RefObject, TransitionEventHandler } from 'react';
import { useCallback, useRef, useState } from 'react';

export const DEFAULT_EXPANDED_LOADING_HEIGHT = '300px';

export const animateExpandRecommendedStyle = {
	overflow: 'hidden',
	transition: 'height 0.5s',
} as const;

/**
 * The height to expand to if the height of the content could not be computed.
 *
 * This should never actually get used, but it makes things more robust.
 */
const fallbackExpandedHeight = 'auto';

const fallbackErrorMessage = `
You may have noticed that your expanded element
suddenly jumped from 0 to full height. This is
because \`useAnimateExpand\` was unable to complete
its full transition lifecycle, so a fallback was
triggered to ensure that the element remained visible.
The likely reason is that your component did not render
completely on initialization. Common causes include
the loading of missing translation files (i.e. calling
\`t('users:something')\` without including 'users'
in \`useTranslation\`).
`
	.replaceAll('\n', ' ')
	.trim();

/**
 * When the container has finished expanding, set its height to `auto`
 * so that it will resize if the content changes.
 */
const transitionEndHeight = 'auto';

function preferredHeight(element: HTMLElement): number | string {
	const calcHeight =
		element.offsetHeight -
		element.clientHeight +
		element.scrollHeight +
		// This plus one ensures that there is a difference between initial and updated height
		// of the elements. If they are the same, a transition will not occur thus the
		// onTransitionEnd callback will not be run
		1;

	if (calcHeight === 1 && process.env.NODE_ENV !== 'production') {
		console.warn(fallbackErrorMessage);
	}

	return calcHeight === 1 ? fallbackExpandedHeight : calcHeight;
}

export interface AnimateExpandOptions {
	initialCollapsed?: boolean;
	/**
	 * The height of the container when it is collapsed.
	 *
	 * This MUST be in absolute units like `px` or `rem`. Values
	 * such as `auto` do not work with CSS transitions.
	 */
	collapsedHeight: CSSProperties['height'];

	onExpandComplete?: () => void;
}

export interface AnimateExpandReturn<E extends HTMLElement> {
	/**
	 * Use this to switch between content for the collapsed and expanded states.
	 *
	 * Note: The expanded height is computed one frame after this changes from true to false.
	 *       The expanded content must immediately have the height that you want to transition to.
	 *       If the content needs time to load, guess the height that it will be and display
	 *       a loading placeholder with that same height. When the animation is finished
	 *       the container will switch to `height: auto` and thus it will update to the correct
	 *       height when the content finishes loading.
	 */
	collapsed: boolean;
	collapse: () => void;
	expand: () => void;

	containerProps: {
		ref: RefObject<E>;
		style: Pick<CSSProperties, 'height'> & typeof animateExpandRecommendedStyle;
		onTransitionEnd: TransitionEventHandler<E>;
	};
}

/**
 * Smoothly animate an element's height between a collapsed state and an expanded state.
 *
 * @deprecated Use AnimateExpand instead
 */
export function useAnimateExpand<E extends HTMLElement>(
	options: AnimateExpandOptions
): AnimateExpandReturn<E> {
	const {
		collapsedHeight,
		initialCollapsed = true,
		onExpandComplete,
	} = options;

	const containerRef = useRef<E>(null);

	const [collapsed, setCollapsed] = useState(initialCollapsed);

	const [height, setHeight] = useState<CSSProperties['height']>(
		initialCollapsed ? collapsedHeight : fallbackExpandedHeight
	);

	const expand = useCallback(() => {
		setCollapsed(false);

		// Wait for a moment so that the content can change and then set
		// the container's height so that it will expand to fit the content.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				setHeight(
					containerRef.current
						? preferredHeight(containerRef.current)
						: fallbackExpandedHeight
				);
			});
		});
		// Need to set height to auto so it will fit dynamically added content
		setTimeout(() => {
			setHeight(fallbackExpandedHeight);
			onExpandComplete?.();
		}, 1000);
	}, [onExpandComplete]);

	const collapse = useCallback(() => {
		// Transitions do not work with dynamic values such as `auto`.
		// Set the container's style height to its current height in px
		// to make sure that the transition will work.
		setHeight(containerRef.current?.offsetHeight ?? fallbackExpandedHeight);

		setCollapsed(true);

		// Wait for the height style change to register.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				// Now set the container's style height so that it starts collapsing.
				setHeight(collapsedHeight);
			});
		});
	}, [collapsedHeight]);

	const onTransitionEnd = useCallback<TransitionEventHandler<E>>(
		(event) => {
			if (event.propertyName === 'height' && !collapsed) {
				setHeight(transitionEndHeight);
			}
		},
		[collapsed]
	);

	return {
		collapsed,
		collapse,
		expand,

		containerProps: {
			ref: containerRef,
			style: { ...animateExpandRecommendedStyle, height },
			onTransitionEnd,
		},
	};
}
